// screens/create-product-mgt/ProductCreation.jsx
import React, { useState } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import Header from '../../layouts/Header';
import AddProduct from './AddProduct';
import AllocateMenuPrice from './AllocateMenuPrice';
import AllocateAddon from './AllocateAddon';
import AllocateIngredients from './AllocateIngredients';
import { toast } from 'react-toastify';

// ─── FIX 1: Removed unused Button, Spinner, Alert imports ───────────────────

const ProductCreation = () => {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [productCreated, setProductCreated] = useState(false);

  const [productData, setProductData] = useState({
    product: {
      sequenceNumber: "", category: "", name: "", description: "",
      hsn: "", taxPercent: "", makingTime: "", mrp: "", offerPrice: "",
      productCost: 0, productTagId: "", productTagName: "", keywords: [],
      foodType: "", classType: "", calories: "", weight: "", weightUnit: "",
      isEventProduct: "", isNewProduct: "", isFreebieProduct: "",
      orderType: "ALL", excludedOutlets: [], productsNature: [],
      displayImage: null, coverPicture: null
    },
    pricing: { menuGroups: [], pricingStrategy: "uniform", uniformPrice: 0, isAllocated: false },
    addons: { selectedAddons: [], maxSelections: 0, isRequired: false },
    ingredients: { items: [], totalCost: 0, wastagePercentage: 5, recipeInstructions: "" }
  });

  // ─── FIX 2: Mark steps 3 & 4 as optional ────────────────────────────────
  const steps = [
    { id: 1, title: "Product Details",   component: AddProduct,           optional: false },
    { id: 2, title: "Menu Pricing",      component: AllocateMenuPrice,    optional: false },
    { id: 3, title: "Add-ons",           component: AllocateAddon,        optional: true  },
    { id: 4, title: "Ingredients",       component: AllocateIngredients,  optional: true  },
  ];

  const updateStepData = (step, data) => {
    setProductData(prev => ({ ...prev, [step]: { ...prev[step], ...data } }));
  };

  const handleNext = () => {
    setErrors([]);
    setProductCreated(true);
    if (currentStep < steps.length) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setErrors([]);
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleComplete = () => {
    setProductData({
      product: {
        sequenceNumber: "", category: "", name: "", description: "",
        hsn: "", taxPercent: "", makingTime: "", mrp: "", offerPrice: "",
        productCost: 0, productTagId: "", productTagName: "", keywords: [],
        foodType: "", classType: "", calories: "", weight: "", weightUnit: "",
        isEventProduct: "", isNewProduct: "", isFreebieProduct: "",
        orderType: "ALL", excludedOutlets: [], productsNature: [],
        displayImage: null, coverPicture: null
      },
      pricing: { menuGroups: [], pricingStrategy: "uniform", uniformPrice: 0, isAllocated: false },
      addons: { selectedAddons: [], maxSelections: 0, isRequired: false },
      ingredients: { items: [], totalCost: 0, wastagePercentage: 5, recipeInstructions: "" }
    });
    localStorage.removeItem("productId");
    setProductCreated(false);
    setCurrentStep(1);
    setErrors([]);
    setIsSubmitting(false);
    toast.success("Product created! You can now create another.", {
      position: "top-right", autoClose: 3000, theme: "light",
    });
  };

  const renderStep = () => {
    const StepComponent = steps.find(s => s.id === currentStep)?.component;
    if (!StepComponent) return null;

    if (currentStep === 4) {
      return (
        <AllocateIngredients
          data={productData} onNext={handleNext} onBack={handleBack}
          updateData={updateStepData} currentStep={currentStep}
          totalSteps={steps.length} isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting} setErrors={setErrors}
          onComplete={handleComplete}
        />
      );
    }

    return (
      <StepComponent
        data={productData} onNext={handleNext} onBack={handleBack}
        updateData={updateStepData} currentStep={currentStep}
        totalSteps={steps.length} isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting} setErrors={setErrors}
        isProductCreated={productCreated}
      />
    );
  };

  // ─── Derived: product name for context bar (available after step 1 saves) ─
  const savedProductName = productData.product?.name || null;
  const savedProductId   = productData.product?._id  || null;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="container" style={{ backgroundColor: "white" }}>

          {/* ─── Header row ─────────────────────────────────────────────────
              FIX 3: Removed the redundant blue "Step X" badge from top-right.
              The stepper below already communicates current position.       */}
          <Row className="pt-3">
            <Col>
              <h5 className="fw-semibold mb-1">Create Product</h5>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                Step {currentStep} of {steps.length} — {steps.find(s => s.id === currentStep)?.title}
              </p>
            </Col>
            <hr className="mt-3" />
          </Row>

          {/* ─── Stepper ─────────────────────────────────────────────────── */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between position-relative">

                {/* Background track */}
                <div style={{
                  position: 'absolute', top: 19, left: '12.5%', right: '12.5%',
                  height: 2, background: '#e9ecef', zIndex: 0,
                }} />

                {/* FIX 4: Completed segment fills green up to current step */}
                <div style={{
                  position: 'absolute', top: 19, left: '12.5%',
                  width: `${((currentStep - 1) / (steps.length - 1)) * 75}%`,
                  height: 2, background: '#28a745', zIndex: 1,
                  transition: 'width 0.35s ease',
                }} />

                {steps.map((step) => {
                  const isActive    = step.id === currentStep;
                  const isCompleted = step.id < currentStep;
                  return (
                    <div key={step.id} className="text-center" style={{ flex: 1, position: 'relative', zIndex: 2 }}>
                      <div style={{
                        width: 38, height: 38, lineHeight: '36px',
                        borderRadius: '50%', margin: '0 auto',
                        fontWeight: 600, fontSize: 13,
                        border: isActive ? '2px solid #0d6efd' : '2px solid transparent',
                        background:   isCompleted ? '#28a745' : isActive ? '#0d6efd' : '#e9ecef',
                        color:        isCompleted || isActive ? '#fff' : '#6c757d',
                        transform:    isActive ? 'scale(1.1)' : 'scale(1)',
                        transition:   'all 0.3s ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isCompleted ? '✓' : step.id}
                      </div>

                      {/* Step label */}
                      <div style={{ fontSize: 11, fontWeight: 500, marginTop: 5,
                        color: isActive ? '#0d6efd' : isCompleted ? '#28a745' : '#6c757d' }}>
                        {step.title}
                      </div>

                      {/* FIX 5: Show "(optional)" under steps 3 & 4 */}
                      {step.optional && (
                        <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 1 }}>
                          optional
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>

          {/* ─── FIX 6: Sticky product context bar ──────────────────────────
              After step 1 creates the product, show its name + ID so the
              user always knows which product they're configuring.          */}
          {savedProductId && (
            <div style={{
              background: '#f0f7ff', border: '1px solid #bfd9f5',
              borderRadius: 8, padding: '8px 16px',
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 16, fontSize: 13,
            }}>
              <span style={{ color: '#0d6efd', fontWeight: 600 }}>✓</span>
              <span style={{ color: '#374151' }}>
                Product: <strong>{savedProductName}</strong>
              </span>
              <span style={{ color: '#9ca3af', fontSize: 11 }}>ID: {savedProductId}</span>
            </div>
          )}

          {/* ─── Error display ───────────────────────────────────────────── */}
          {errors.length > 0 && (
            <Row className="mb-3">
              <Col>
                <div className="alert alert-danger alert-dismissible" role="alert">
                  <strong>Please fix the following:</strong>
                  <ul className="mb-0 mt-1">
                    {errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                  <button type="button" className="btn-close"
                    onClick={() => setErrors([])} aria-label="Close" />
                </div>
              </Col>
            </Row>
          )}

          {/* ─── Step content ────────────────────────────────────────────── */}
          <Row>
            <Col>
              <Card className=" border-0">
                <Card.Body className="p-0 bg-white">
                  {renderStep()}
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductCreation;